import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep, uniq } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { UniversalSearchModel, UniversalSearchModelType } from 'src/app/core/models/universal-search.model';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { SectorIndustry } from 'src/app/results/sidebar/services/sector-industry.service';
import { CONTINENT_SECTIONS } from 'src/app/shared/constants';
import { ContinentSection, CountryInterface, IndustryItemDto, MatchedIndustry, StateInterface } from 'src/app/shared/dto';
import { CompanySearchCriteria, CopilotMetro, DepartmentSizeData, EmployeeSizeCriteria, RevenueCriteria, VisitorIntelCriteria } from 'src/app/shared/dto/copilot-search.dto';
import { AccountAttributesEnum, FeatureFlagsEnum, FundingFilterTypeEnum, REQUEST_TYPE, SidebarTabTypeEnum } from 'src/app/shared/enums';
import { LoadingService, LocationsService, RequestBodyService } from 'src/app/shared/services';
import { CompaniesApiService, SearchApiService } from 'src/app/shared/services/api';
import { JOB_LABELS } from 'src/app/shared/services/job_labels.service';
import { IndustryFilterType, SearchService } from 'src/app/results/search/services/search.service';
import { UserService } from 'src/app/core/services/user.service';
import { environment } from 'src/environments/environment';
import { TreeviewItem } from 'src/app/shared/components/tree-view';
import { calculateMatchingIndex, stringToNormalizedArrayOfWords } from 'src/app/shared/helpers';
import { FuzzySearchService } from 'src/app/shared/services/fuzzy-search.service';

// TODO: move these services to shared
import { TechIntelCategory } from '../../../results/sidebar/services/tech-intel-category.service';
import { MetroService } from '../../../results/sidebar/services/metro-data.services';
import { JobDepartmentCategoryData } from '../../../results/sidebar/services/job-department-category-data.service';
import { TechIntelCategoryData } from '../../../results/sidebar/services/tech-intel-category-data.services';

const GET_COPILOT_SEARCH_TAG: string = 'get-copilot-search-tag';
const MINIMUM_FUZZY_MATCH_SCORE: number = 75;
const domainRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@UntilDestroy()
@Component({
    selector: 'app-search-box',
    templateUrl: './search-box.component.html',
    standalone: false
})
export class SearchBoxComponent implements OnInit {
    @Input() isSearchInProgress;
    @Input() areFiltersEdited;
    @Input() responseResult;
    @Output() searchTriggered = new EventEmitter();

    readonly isLoading$: Observable<boolean> = this.loadingService.getIsLoading(GET_COPILOT_SEARCH_TAG);

    welcomeCompany: string[];
    searchText: string;
    jobLabels = cloneDeep(JOB_LABELS);
    isCopilotSearch: boolean = false;
    exampleSearches: string[] = [
        "Show me Vice Presidents in the IT industry in Massachusetts.",
        "Show me companies in the media industry with over 1M in revenue",
        "Give me companies in California in the manufacturing industry",
        "Give me Directors of Marketing"
    ];

    readonly featureFlagsEnum: typeof FeatureFlagsEnum = FeatureFlagsEnum;

    // Event-driven; the `areFiltersEdited` @Input can lag behind the search-performed event.
    private readonly areFiltersEditedSinceSearch: WritableSignal<boolean> = signal(false);

    constructor(
        private readonly requestBodyService: RequestBodyService,
        private readonly authenticationService: AuthenticationService,
        private readonly searchApiService: SearchApiService,
        private readonly locationsService: LocationsService,
        private readonly sectorIndustry: SectorIndustry,
        private readonly searchService: SearchService,
        private readonly userService: UserService,
        private readonly companiesApiService: CompaniesApiService,
        private readonly technoIntelCategory: TechIntelCategory,
        private readonly fuzzySearchService: FuzzySearchService,
        private readonly metroService: MetroService,
        private readonly loadingService: LoadingService
    ) { }

    ngOnInit(): void {
        this.searchText = this.searchService.searchTextInput || '';
        this.welcomeCompany = this.searchService.chipTextInput || [];
        this.requestBodyService.getFiltersEditedEvent().pipe(untilDestroyed(this)).subscribe(({ edited }) => {
            this.areFiltersEditedSinceSearch.set(!!edited);
        });
        this.searchService.getSearchPerformedEvent(SidebarTabTypeEnum.SALES_INTEL).pipe(untilDestroyed(this)).subscribe(() => {
            if (!this.isCopilotSearch && (this.areFiltersEditedSinceSearch() || this.areFiltersEdited)) {
                this.clearSearchInputs();
            }
            this.areFiltersEditedSinceSearch.set(false);
            this.isCopilotSearch = false;
        });
        this.requestBodyService.clearSearchSidebar.pipe(untilDestroyed(this)).subscribe((isClear) => {
            if (isClear) {
                this.clearSearchInputs();
            }
        })
    }

    onSearchCompany(): void {
        const searchModelData: UniversalSearchModelType = {
            ...cloneDeep(UniversalSearchModel),
            company_names: [...(this.welcomeCompany || [])],
            company_domains: [...(this.welcomeCompany || [])],
            request_type: REQUEST_TYPE.COMPANIES,
            page: 1,
            verified: 1,
            tiers:this.authenticationService.getHumanVerifiedTiers().toString()
        }
        this.searchService.chipTextInput = this.welcomeCompany;
        this.searchTriggered.emit({searchModelData, assitedSearch: false});
    }

    onCopilotSearch(): void {
        const searchText = this.searchText?.trim();
        if (!searchText || this.isSearchInProgress || this.isCopilotSearch) {
            return;
        }
        const parts = searchText.split(/\s+/);
        this.isCopilotSearch = true;
        this.searchService.searchTextInput = searchText;
        if (parts.length === 1) {
            if (emailRegex.test(searchText)) {
                const emaiObj = { email: searchText };
                this.setCompaniesSearch(emaiObj);
            } else if (domainRegex.test(searchText)) {
                const domainObj = { company_names: [searchText], company_domains: [searchText] }
                this.setCompaniesSearch(domainObj);
            } else {
                const singleWordObj = { company_names: [searchText], company_domains: [searchText] }
                this.setCompaniesSearch(singleWordObj);
            }
        } else {
            this.getSearchCriteriaUsingAI(searchText);
        }
    }

    setCompaniesSearch(searchObj): void {
        const { company_technographic_products, company_technographic_vendors, ...rest } = cloneDeep(UniversalSearchModel)
        //@ts-ignore
        const searchModelData: UniversalSearchModelType = {
            ...rest,
            ...searchObj
        }
        this.searchTriggered.emit({ searchModelData, assitedSearch: false });
    }

    getSearchCriteriaUsingAI(searchText: string): void {
        this.getSearchCriteria().pipe(
            filter(Boolean),
            switchMap((searchResponse: CompanySearchCriteria) => {
                return (Array.isArray(searchResponse.keywords) && searchResponse.keywords.length > 0)
                    ? this.companiesApiService.getPopularKeywords("").pipe(
                        map(rsp => ({
                            popularKeywords: rsp['matchingKeywords'] || [],
                            searchResponse
                        }))
                    )
                    : of({ popularKeywords: [], searchResponse });
            }),
            map(({ popularKeywords, searchResponse }) => {
                if(searchResponse.job_departments.length !== 0 && searchResponse.job_levels.length !== 0 && searchResponse.job_titles.length !== 0) {
                    searchResponse = this.fuzzyMatchJobs(searchResponse);
                }
                const searchModelData = {
                    company_names: [...(searchResponse.company_names || []), ...(searchResponse.company_domains || [])],
                    company_domains: [...(searchResponse.company_names || []), ...(searchResponse.company_domains || [])],
                    email: searchResponse.email,
                    first_name: searchResponse.first_name,
                    last_name: searchResponse.last_name,
                    page: 1,
                    verified: 1,
                    company_location_zipcodes: [...(searchResponse.company_zipcodes || [])],
                    contact_location_zipcodes: searchResponse.contact_zipcodes.join(","),
                    is_international: this.userService.hasAccess(AccountAttributesEnum.INTERNATIONAL) ? null : 0,
                    tiers: this.authenticationService.getHumanVerifiedTiers().toString(),
                    viUUID: searchResponse.visitor_intel_criteria?.viSearchDurationType ? this.userService.getUserAccountUUID() : "",
                    ...this.updateRequestType(searchResponse.search_type),
                    ...this.updateLocationData(searchResponse),
                    ...this.updateOfficeTypes((({company_location_types, contact_location_types}) => ({company_location_types, contact_location_types}))(searchResponse)),
                    ...this.updateRevenueCriteria(searchResponse.revenue_criteria),
                    ...this.updateEmployeeSizeCriteria(searchResponse.employee_size_criteria),
                    ...this.updateJobTitles(searchResponse.job_titles),
                    ...this.updateJobLevels(searchResponse.job_levels),
                    ...this.updateJobDepartments(searchResponse.job_departments),
                    ...this.updateIndustryData((({industries, industries_data, naics_codes, sic_codes}) => ({industries, industries_data, naics_codes, sic_codes}))(searchResponse)),
                    ...this.updateVisitorIntelCriteria(searchResponse.visitor_intel_criteria),
                    ...this.updateKeywords(popularKeywords, searchResponse.keywords),
                    ...this.updateTechnographics(searchResponse.technologies),
                    ...this.updateMetroArea((({company_metro_areas, contact_metro_areas}) => ({company_metro_areas, contact_metro_areas}))(searchResponse)),
                    ...this.updateDepartmentSize(searchResponse.department_size_criteria),
                    ...this.updateFunding(searchResponse),
                }
                return searchModelData;
            }),
            untilDestroyed(this)).subscribe((searchModelResponse) => {
                this.isCopilotSearch = true;
                const { company_technographic_products, company_technographic_vendors, ...rest } = cloneDeep(UniversalSearchModel)
                //@ts-ignore
                const searchModelData: UniversalSearchModelType = {
                    ...rest,
                    ...searchModelResponse
                }
                this.searchTriggered.emit({searchModelData, assitedSearch: true});
            })
    }

    private updateFunding(searchResponse) {
        const fundingAmount = {};
        if(searchResponse.funding_criteria && searchResponse.funding_criteria.length !== 0) {
            searchResponse.funding_criteria.forEach((el) => {
                if (el.limit_type === "MINIMUM") {
                    fundingAmount["min_funding_amount"] = el["funding_amount"] ? el["funding_amount"] : null;
                    fundingAmount["min_funding_amount_identifier"] = el["funding_unit_type"] ? (el["funding_unit_type"].charAt(0) === 'T' ? 'K' : el["funding_unit_type"].charAt(0)) : 'M';
                } else if (el.limit_type === "MAXIMUM") {
                    fundingAmount["max_funding_amount"] = el["funding_amount"] ? el["funding_amount"] : null;
                    fundingAmount["max_funding_amount_identifier"] = el["funding_unit_type"] ? (el["funding_unit_type"].charAt(0) === 'T' ? 'K' : el["funding_unit_type"].charAt(0)) : 'M';
                }
            })
        }
        return {
            "last_funding_rounds": searchResponse["funding_round"] ? [searchResponse["funding_round"]] : null,
            "funding_type": searchResponse["is_last_round_funding"] && searchResponse["is_last_round_funding"] === "Yes" ? FundingFilterTypeEnum.LAST_ROUND : FundingFilterTypeEnum.TOTAL,
            ...fundingAmount
        }
    }

    private updateRequestType(searchType: REQUEST_TYPE | null): {request_type: REQUEST_TYPE} {
        return { request_type: searchType ? searchType : this.searchService.searchModelData.request_type };
    }

    private updateMetroArea(cpMetros: CopilotMetro):
        Partial<Pick<UniversalSearchModelType, 'company_location_metro' | 'contact_location_metro' | 'location_country_codes'>> {
            let result = {};

            if (cpMetros.company_metro_areas?.length) {
                result['company_location_metro'] = this.getMatchingMetroCodes(cpMetros.company_metro_areas);
            }
            if (cpMetros.contact_metro_areas?.length) {
                result['contact_location_metro'] = this.getMatchingMetroCodes(cpMetros.contact_metro_areas);
            }

            if (result['company_location_metro']?.length || result['contact_location_metro']?.length) {
                result ['location_country_codes'] = ['US'];
            }

            return result;
    }

    private getMatchingMetroCodes(metro: string[]): string[] {
        return metro.reduce((arr: string[], cpMetro: string) => {
            const cpMetroArr = this.splitByLastComma(cpMetro);

            const matchers = [
              (uiMetroArr: string[]) => uiMetroArr[0] === cpMetroArr[0] && uiMetroArr[1] === cpMetroArr[1],
              (uiMetroArr: string[]) => uiMetroArr[0].includes(cpMetroArr[0]) && uiMetroArr[1] === cpMetroArr[1],
              (uiMetroArr: string[]) => uiMetroArr[0] === cpMetroArr[0] && uiMetroArr[1] !== cpMetroArr[1],
              (uiMetroArr: string[]) => uiMetroArr[0].includes(cpMetroArr[0]) && uiMetroArr[1] !== cpMetroArr[1],
            ];

            let matchedObj: {name:string, code:string} | undefined;

            for (const matcher of matchers) {
              matchedObj = this.metroService.getMetroData().find((metro) => matcher(this.splitByLastComma(metro.name)));
              if (matchedObj) break;
            }

            if (matchedObj) arr.push(matchedObj.code);
            return arr;
          }, []);
    }

    private updateLocationData(searchResponse: CompanySearchCriteria): { location_country_codes?: string[], company_location_states?: string[] } {
        let result: {
            contact_location_states: string[];
            company_location_states: string[];
            location_country_codes: string[];
        } = {contact_location_states: null, company_location_states: null, location_country_codes: ['US']};
        if (searchResponse.company_countries?.length || searchResponse.contact_countries?.length) {
            const countryAbbreviation = CONTINENT_SECTIONS.reduce((result: string[], section: ContinentSection | CountryInterface) => {
                if ("countryCode" in section) {
                    if (searchResponse.company_countries.includes(section.countryName) ||
                        searchResponse.contact_countries.includes(section.countryName)) {
                        result.push(section.countryCode);
                    }
                }
                else if ("continentName" in section &&
                         (searchResponse.company_countries.includes(section.continentName) ||
                          searchResponse.contact_countries.includes(section.continentName))) {
                    result.push(...section.countries.map(country => country.countryCode));
                }
                else {
                    result.push(...section.countries
                        .filter(country =>
                            searchResponse.company_countries.includes(country.countryName) ||
                            searchResponse.contact_countries.includes(country.countryName))
                        .map(country => country.countryCode)
                    );
                }
                return result;
            }, []);
            result = {
                ...result, location_country_codes: countryAbbreviation.length !== 0 ? countryAbbreviation : ['US']
            };
        }
        if (searchResponse.company_states?.length || searchResponse.contact_states?.length) {
            const locations = this.locationsService.getLatestLocations();

            const allStates:StateInterface[] = locations.reduce((result: StateInterface[], country: CountryInterface) => {
                if (country.states) {
                    result.push(...country.states);
                }
                return result;
            }, []);

            const companyStatesLower: string[] = searchResponse.company_states?.map(state => state.toLowerCase()) ?? [];
            const contactStatesLower: string[] = searchResponse.contact_states?.map(state => state.toLowerCase()) ?? [];

            const matchedCompanyStates: StateInterface[] = allStates.filter((state: StateInterface) => companyStatesLower?.includes(state.stateName?.toLowerCase()) && state.stateCodeIsAbbreviation);
            const companyState: StateInterface = this.getMatchedState(matchedCompanyStates);
            if (companyState) {
                result = {
                    ...result, location_country_codes: [companyState?.countryCode], company_location_states: [companyState?.stateCode]
                };
            }

            const matchedStates : StateInterface[] = allStates.filter((state: StateInterface) => contactStatesLower?.includes(state.stateName?.toLowerCase()) && state.stateCodeIsAbbreviation);
            const contactState: StateInterface = this.getMatchedState(matchedStates)
            if (contactState) {
                result = {
                    ...result, location_country_codes: [contactState?.countryCode], contact_location_states: [contactState.stateCode]
                };
            }
        }
        return result;
    }

    private getMatchedState(matchedStates: StateInterface[]) : StateInterface{
        return matchedStates.length > 1 ? matchedStates.find(state => state.countryCode === "US") : matchedStates?.[0];
    }

    updateOfficeTypes(officeTypes?: Partial<Pick<CompanySearchCriteria, 'company_location_types' | 'contact_location_types'>>): {company_location_branch?: boolean, company_location_hq?: boolean, location_types?: string[];} {
        const company_types = officeTypes.company_location_types;
        const contact_types = officeTypes.contact_location_types;

        let company_location_hq = true;
        let company_location_branch = true;

        if (company_types?.length) {
            const hq_type = company_types.find((type) => type.location_type.toLowerCase() === 'hq');
            const branchPlural = company_types.find((type) => type.location_type.toLowerCase() === 'branches');
            if (branchPlural) branchPlural.location_type = 'branch';

            const branch_type = company_types.find((type) => type.location_type.toLowerCase() === 'branch');
            if (hq_type) {
                company_location_hq = hq_type.is_included;
                company_location_branch = !hq_type.is_included;
            };
            if (branch_type) {
                company_location_branch = branch_type.is_included;
                if (!hq_type) {
                    company_location_hq = !branch_type.is_included;
                }
            }
            if (!company_location_branch && !company_location_hq) {
                company_location_branch = true;
                company_location_hq = true;
            }
        }

        const location_types = [];

        if (contact_types?.length) {
            const branchPlural = contact_types.find((type) => type.location_type.toLowerCase() === 'branches');
            const remotePlural = contact_types.find((type) => type.location_type.toLowerCase() === 'remotes');
            if (branchPlural) branchPlural.location_type = 'branch';
            if (remotePlural) remotePlural.location_type = 'remote';

            contact_types.forEach((type) => {
                if (type.is_included && ['hq', 'branch', 'remote'].includes(type.location_type.toLocaleLowerCase())) location_types.push(type.location_type.toLowerCase());
            });

            if (!location_types.length) location_types.push(
                ...['hq', 'branch', 'remote'].filter((type) => !contact_types.find((t) => t.location_type.toLowerCase() === type && !t.is_included))
            );
            if (location_types.includes('hq')) location_types.push('hqFallback');
            if (!location_types.length) location_types.push('hq', 'branch', 'remote', 'hqFallback');
        }

        return {company_location_hq, company_location_branch, location_types}
    }

    updateDepartmentSize(departmentSizes?: DepartmentSizeData[]): {department_size: {department: string, min_size: string, max_size: string}[]} {
        if (departmentSizes?.length) {
            const {department_name} = departmentSizes[0];
            const departmentsList = JOB_LABELS.departments;
            const department = Object.keys(departmentsList).find((key) =>
                departmentsList[key].toLowerCase() === department_name.toLowerCase() ||
                department_name.toLowerCase().includes(departmentsList[key].toLowerCase()) ||
                departmentsList[key].toLowerCase().includes(department_name.toLowerCase()));

            const min_size = String(departmentSizes.find((size) => size.limit_type === "MINIMUM")?.employee_count || "");
            const max_size = String(departmentSizes.find((size) => size.limit_type === "MAXIMUM")?.employee_count || "");
            if (department && (min_size || max_size)) {
                return {
                    department_size: [{ department, ...(min_size && { min_size }), ... (max_size && { max_size }) }]
                }
            }
        }
        return {department_size: []};
    }

    private updateIndustryData(inputIndustries?: Partial<Pick<CompanySearchCriteria, 'industries' | 'industries_data' | 'naics_codes' | 'sic_codes'>>):
    { company_naics_codes?: string, company_sic_codes?: string, company_naics_codes_any?: string, company_sic_codes_any?: string } {
        const industriesList = inputIndustries.industries || [];
        const industriesDataList = inputIndustries.industries_data || [];

        const industries = industriesList.map((industry) => {
            const industryDataObject = industriesDataList.find((i) => i.industry_name === industry);
            return {industry_name: industry, similar_industries: industryDataObject?.similar_industries || [] }
        });

        console.log("");
        console.log("");
        console.log("");
        console.log("Combined industries from Copilot:", industries)

        const matchesList = []
        const matchedIndustriesList = [];
        const industryList: IndustryItemDto[] = [...this.sectorIndustry.industryResponse?.industries || []];

        for (const industryObject of industries) {
            console.log("");
            console.log(`************ Searching matches for the Main Industry ("${industryObject.industry_name}") ************`);

            const mainIndustryMatches = this.getMatches(industryObject.industry_name)
            let industryObjectAllMatches = [...mainIndustryMatches];

            if (!mainIndustryMatches.length || mainIndustryMatches[0].score < MINIMUM_FUZZY_MATCH_SCORE) {
                console.log(`Main Industry ("${industryObject.industry_name}") - NO Matches above Min Score level found`)
            } else {
                console.log(`Main Industry ("${industryObject.industry_name}") Matches:`, mainIndustryMatches, `(${mainIndustryMatches.length} ${mainIndustryMatches[0].item.naics < 100 ? "SECTOR(s)" : "INDUSTRY(s)"} ABOVE the Min Score Level)`)
            }

            if (mainIndustryMatches[0].score > MINIMUM_FUZZY_MATCH_SCORE) {
                matchesList.push(...mainIndustryMatches);
            } else {
                for (const similarIndustry of industryObject.similar_industries) {
                    console.log("");
                    console.log(`####### Searching matches for the Similar Industry ("${similarIndustry}")`);

                    const similarIndustryMatches = this.getMatches(similarIndustry);

                    if (!similarIndustryMatches.length || similarIndustryMatches[0].score < MINIMUM_FUZZY_MATCH_SCORE) {
                        console.log(`Similar Industry ("${similarIndustry}") - NO Matches above Min Score level found`)
                    } else {
                        console.log(`Similar Industry ("${similarIndustry}") Matches:`, similarIndustryMatches, `(${similarIndustryMatches.length} ${similarIndustryMatches[0].item.naics < 100 ? "SECTOR(s)" : "INDUSTRY(s)"} ABOVE the Min Score Level)`)
                    }

                    industryObjectAllMatches = [...industryObjectAllMatches, ...similarIndustryMatches];
                    if (similarIndustryMatches[0].score > MINIMUM_FUZZY_MATCH_SCORE) {
                        matchesList.push(...similarIndustryMatches);
                    }
                }
            }

            console.log("");
            if (!industryObjectAllMatches.find((match) => match.score > MINIMUM_FUZZY_MATCH_SCORE)) {
                const bestMatchUnderMinScore = industryObjectAllMatches.reduce((maxScoreMatch, match) => (!maxScoreMatch || match.score > maxScoreMatch.score) ? match : maxScoreMatch, undefined);
                matchesList.push(...[bestMatchUnderMinScore]);

                console.log("...using Best match Under (or equal to) Min Score:", bestMatchUnderMinScore, `(1 ${bestMatchUnderMinScore.item.naics < 100 ? "SECTOR" : "INDUSTRY"})`)
            } else if (mainIndustryMatches[0].score > MINIMUM_FUZZY_MATCH_SCORE) {
                console.log("...using Main Industry Matches.");
            } else {
                console.log("...using Similar Industries Matches (which are above the Min Score Level).");
            }
        }

        console.log("");
        console.log("All matches (Industries + Sectors):", matchesList)

        matchedIndustriesList.push(
            ...this.getIndustriesFromMatches(matchesList, industryList)
                .filter((item, pos, self) => self.findIndex(v => v.industry === item.industry) === pos)
        );

        console.log("")
        console.log("All the Matched Industries (incl. those from Sectors):", matchedIndustriesList);

        const combinedNaicsCodes = [
            ...matchedIndustriesList.map((el) => el.naics),
            ...(inputIndustries.naics_codes?.length ? inputIndustries.naics_codes : []),
        ].map((el) => String(el)).filter((el, i, self) => i === self.indexOf(el)).join(',');

        return this.searchService.industrySearchType === IndustryFilterType.NAME ?
            {
                company_naics_codes_any: combinedNaicsCodes,
                company_sic_codes_any: inputIndustries.sic_codes?.length ?  inputIndustries.sic_codes.join(',') : "",
            } :
            {
                company_naics_codes: combinedNaicsCodes,
                company_sic_codes: inputIndustries.sic_codes?.length ?  inputIndustries.sic_codes.join(',') : "",
            }
    }

    getIndustriesFromMatches(matches: MatchedIndustry[], industryList: IndustryItemDto[]): IndustryItemDto[] {
        return matches.reduce((arr, el) => el.item.naics < 100 ?
                    [...arr, ...industryList.filter((listIndustry) => listIndustry.sector === el.item.sector)] :
                    [...arr, el.item], []);
    }

    getMatchingScore(listItem, inputIndustry, isSector):number {
        const listItemWords = stringToNormalizedArrayOfWords(listItem[isSector ? 'sector' : 'industry']);
        const inputWords = stringToNormalizedArrayOfWords(inputIndustry);
        let wordsScoresSum = 0;

        for (const inputWord of inputWords) {
            let bestMatchScore = 0;
            for (const listItemWord of listItemWords) {
                const score = calculateMatchingIndex(inputWord, listItemWord);
                if (score > bestMatchScore) {
                    bestMatchScore = score;
                }
            }
            wordsScoresSum += bestMatchScore;
        }
        return wordsScoresSum / inputWords.length;
    }

    getBestMatches(scoredList: MatchedIndustry[]): MatchedIndustry[] {
        const aboveThresholdItems = scoredList.filter((item) => item.score > MINIMUM_FUZZY_MATCH_SCORE);
        return aboveThresholdItems.length ? aboveThresholdItems : [scoredList[0]];
    }

    getMatches(inputIndustry: string): MatchedIndustry[] {
        const industryList: IndustryItemDto[] = [...this.sectorIndustry.industryResponse?.industries || []];
        if (!industryList.length) return [];

        let allIndustriesScored: MatchedIndustry[] = [];
        let allSectorsScored: MatchedIndustry[] = [];

        for (const item of industryList) {
            const isSector = item.naics < 100;
            const score = this.getMatchingScore(item, inputIndustry, isSector);
            isSector ? allSectorsScored.push( {item, score} ) : allIndustriesScored.push( {item, score} );
        }

        allIndustriesScored = allIndustriesScored?.filter((item, pos, self) => self.findIndex(v => v.item.industry === item.item.industry) === pos).sort((a, b) => b.score - a.score);
        allSectorsScored = allSectorsScored?.filter((item, pos, self) => self.findIndex(v => v.item.sector === item.item.sector) === pos).sort((a, b) => b.score - a.score);

        if (allIndustriesScored[0].score > allSectorsScored[0].score) {
            return this.getBestMatches(allIndustriesScored);
        }
        return this.getBestMatches(allSectorsScored);
    }

    private updateRevenueCriteria(revenueCriteria?: RevenueCriteria[]): { contact_revenue_min_identifier?: string, company_min_revenue?: string, contact_revenue_max_identifier?: string, company_max_revenue?: string } {
        if (!revenueCriteria?.length) return;

        return revenueCriteria.reduce((acc, rev) => {
            if (rev.limit_type === 'MINIMUM') {
                return {
                    ...acc,
                    contact_revenue_min_identifier: rev.revenue_unit_type === 'THOUSAND' ? 'K' : rev.revenue_unit_type.charAt(0).toUpperCase(),
                    company_min_revenue: rev.revenue_amount
                };
            } else if (rev.limit_type === 'MAXIMUM') {
                return {
                    ...acc,
                    contact_revenue_max_identifier: rev.revenue_unit_type === 'THOUSAND' ? 'K' : rev.revenue_unit_type.charAt(0).toUpperCase(),
                    company_max_revenue: rev.revenue_amount
                };
            }
            return acc;
        }, {});

    }

    private updateEmployeeSizeCriteria(employeeSizeCriteria?: EmployeeSizeCriteria[]): { company_min_size?: string, company_max_size?: string } {
        if (!employeeSizeCriteria?.length) return;

        return employeeSizeCriteria.reduce((acc, size) => {
            if (size.limit_type === 'MINIMUM') {
                return { ...acc, company_min_size: size.employee_count };
            } else if (size.limit_type === 'MAXIMUM') {
                return { ...acc, company_max_size: size.employee_count };
            }
            return acc;
        }, {});

    }

    private updateJobDepartments(jobDepartments?: string[]): { job_departments: Record<string, boolean> }|{job_sub_departments: string[]} | null {
        if (!jobDepartments?.length) return;
        let result: { job_departments: Record<string, boolean> } | {job_sub_departments: string[]} = {job_departments: Object.keys(this.jobLabels.departments).reduce((result, key) => {
            result[key] = jobDepartments.some(
                dept => dept.toLowerCase() === this.jobLabels.departments[key].toLowerCase()
            );
            return result;
        }, {} as Record<string, boolean>)}

        const selectedSubDepartments: string[] = this.filterTreeViewList(cloneDeep(JobDepartmentCategoryData), jobDepartments, null, true)
        if (selectedSubDepartments.length) {
            result = { ...result, job_sub_departments: selectedSubDepartments }
        }

        return result;
    }

    private updateJobLevels(jobLevels?: string[]): { job_levels: Record<string, boolean> } {
        if (!jobLevels?.length) return;

        return {
            job_levels: Object.keys({ ...this.jobLabels.levels_plural, ...this.jobLabels.levels }).reduce(
                (result, key) => ({
                    ...result,
                    [key]: jobLevels.includes(this.jobLabels.levels_plural[key]) || jobLevels.includes(this.jobLabels.levels[key])
                }),
                {} as Record<string, boolean>
            )
        };
    }

    private updateJobTitles(jobTitles?: string[]): { job_title: string } {
        if (!jobTitles?.length) return;

        return { job_title: jobTitles.join(',') }
    }

    updateKeywords(popularKeywords: string[], keywords: string[]) {
        if (!keywords?.length) return;

        if (!popularKeywords?.length) {
            return { specialties: [...keywords] };
        } else {
            const popularKeywordsSet: Set<String> = new Set(popularKeywords.map(keyword => keyword.toLowerCase()));

            let popular: string[] = keywords.filter(keyword => popularKeywordsSet.has(keyword.toLowerCase()));
            let specialties: string[] = keywords.filter(keyword => !popularKeywordsSet.has(keyword.toLowerCase()));

            if (popular.length === 0 || specialties.length === 0) {
                popular = keywords.filter(keyword => {
                    const newKeyword = keyword.replace(/^key/, "");
                    return popularKeywordsSet.has(newKeyword.toLowerCase())
                });
                specialties = keywords.filter(keyword => {
                    const newKeyword = keyword.replace(/^key/, "");
                    return !popularKeywordsSet.has(newKeyword.toLowerCase())
                })
            }

            return popular.length !== 0 ? {popular_keywords: popular} : {specialties: specialties}
        }
    }

    updateVisitorIntelCriteria(visitorIntelCriteria: VisitorIntelCriteria): { viSearchDurationType: string, viDateFrom: string, viDateTo: string } {
        if (!visitorIntelCriteria || !visitorIntelCriteria.viSearchDurationType) return;

        return { viSearchDurationType: visitorIntelCriteria.viSearchDurationType, viDateFrom: new Date(visitorIntelCriteria?.vi_date_from).toLocaleDateString(), viDateTo: new Date(visitorIntelCriteria?.vi_date_to).toLocaleDateString() }
    }

    updateTechnographics(technographics: string[]): { company_technographic_products_bool?: string[], company_technographic_vendors_bool?: string[], company_technographic_sub_categories_bool?: string[]} | null {
        if(!technographics?.length) return;

        let productList: string[] = this.getProductList();
        let vendorList: string[] = this.getVendorList();
        const categoryListTree: TreeviewItem[] = environment.dynamicTechnographics
                ? cloneDeep(this.technoIntelCategory.techIntelCategoryData)
                : TechIntelCategoryData

        const categoryList = this.flattenTreeview(categoryListTree);

        const dataSets = {
            products: productList,
            providers: vendorList,
            categories: categoryList
        };

        const matchedResults = technographics.reduce<{ name: string; type: string }[]>((acc, tech) => {
            return acc.concat(this.fuzzySearchService.searchInDataset(tech, dataSets));
        }, []);

        const technographicProducts = matchedResults
            .filter(item => item.type === "products")
            .map(item => item.name);

        const technographicVendors = matchedResults
            .filter(item => item.type === "providers")
            .map(item => item.name);

        const technographicCategory = matchedResults
            .filter(item => item.type === "categories")
            .map(item => item.name);
            console.log("matchedResults", matchedResults);

        let finalResult: { company_technographic_products_bool?: string[], company_technographic_vendors_bool?: string[], company_technographic_sub_categories_bool?: string[]} = {};

        if (technographicProducts.length) {
            finalResult = { ...finalResult, company_technographic_products_bool : technographicProducts };
        }
        if (technographicVendors.length) {
            finalResult = { ...finalResult, company_technographic_vendors_bool : technographicVendors };
            finalResult["company_technographic_vendors_bool"] = technographicVendors;
        }
        if (technographicCategory.length) {
            const technographicCategorySelected: string[] = this.filterTreeViewList(categoryListTree, technographicCategory)
            finalResult = { ...finalResult, company_technographic_sub_categories_bool : technographicCategorySelected };
        }

        return finalResult;
    }

    private fuzzyMatchJobs(response: CompanySearchCriteria): CompanySearchCriteria {
        const { job_levels, job_departments, job_titles } = response;

        const departmentList = this.flattenTreeview(JobDepartmentCategoryData);

        job_titles.forEach(title => {
            const matchedLevel = job_levels.find(level =>
                title.toLowerCase().includes(level.toLowerCase().replace(/s$/, ""))
            );

            if (matchedLevel) {
                let titleWithoutLevel = this.removeExactMatch(title, matchedLevel.replace(/s$/, ""));

                let matchedDepartment: string | null = job_departments.find(job_department =>
                    this.fuzzySearchService.fuzzyMatchInString(job_department, titleWithoutLevel)
                ) || null;

                if (matchedDepartment) {
                    let finalDepartment = this.fuzzySearchService.searchInArray(matchedDepartment, departmentList)

                    if (finalDepartment) {
                        response = { ...response, job_titles: null };
                    } else {
                        response = { ...response, job_levels: null, job_departments: null };
                    }
                } else {
                    response = { ...response, job_levels: null, job_departments: null };
                }
            } else {
                response = { ...response, job_levels: null, job_departments: null };
            }
        });
        return { ...response };
    }

    private getProductList(): string[] {
        if (!environment.dynamicTechnographics) {
            return this.technoIntelCategory.products();
        }

        const data = this.technoIntelCategory.getTechnographicsProductsVendorsList();
        return uniq((data?.productsList || [])
            .filter(item => item.product)
            .map(item => item.product))
            .sort();
    }

    private getVendorList(): string[] {
        if (!environment.dynamicTechnographics) {
            return this.technoIntelCategory.vendors();
        }

        const data = this.technoIntelCategory.getTechnographicsProductsVendorsList();
        return uniq((data?.productsList || [])
            .filter(item => item.vendor)
            .map(item => item.vendor))
            .sort();
    }

    private filterTreeViewList(treeViewList: any[], userInput: string[], parentText: string | null = null, jobDept = false): string[] {
        let lowerCaseUserInput: string[] = userInput.map(t => t.toLowerCase());

        return treeViewList.reduce((result: string[], item) => {
            const itemTextLower = item.text.toLowerCase();
            if(lowerCaseUserInput.length !== 0) {
                if (lowerCaseUserInput.some(input => itemTextLower.includes(input) || input.includes(itemTextLower))) {
                    lowerCaseUserInput = lowerCaseUserInput.filter(input => !itemTextLower.includes(input) && !input.includes(itemTextLower));
                    if(!parentText) {
                        result = [];
                    }
                    if (jobDept) {
                        parentText && result.push(`${parentText}~~${item.text}`);
                    } else {
                        parentText ? result.push(item.text) : null;
                    }

                    if (item.internalChildren?.length) {
                        result.push(...item.internalChildren.map(child => jobDept ? `${item.text}~~${child.text}` : child.text));
                    }
                    return result;
                }

                if (item.internalChildren?.length) {
                    result.push(...this.filterTreeViewList(item.internalChildren, userInput, item.text, jobDept));
                }
            }
            return result;
        }, []);
    }

    private flattenTreeview(tree: TreeviewItem[]): string[]  {
        let list: string[] = [];
        tree.forEach(dept => {
            list.push(dept.text);
            if (dept.children) {
                list.push(...dept.children.map(child => child.text));
            }
        });
        return list;
    }

    private removeExactMatch(str: string, toRemove: string): string {
        return str.replace(new RegExp(`\\b${toRemove}\\b`, 'i'), '').trim();
    }

    private splitByLastComma(str: string): string[] {
        const lastCommaIndex = str.lastIndexOf(',');
        if (lastCommaIndex === -1) return [str];

        const firstPart = str.slice(0, lastCommaIndex).trim();
        const secondPart = str.slice(lastCommaIndex + 1).trim();
        return [firstPart, secondPart];
    }

    private getSearchCriteria(): Observable<CompanySearchCriteria> {
        return this.loadingService.loadingWrapper(this.searchApiService.getSearchCriteria(this.searchText.trim()), GET_COPILOT_SEARCH_TAG);
    }

    private clearSearchInputs(): void {
        this.searchText = '';
        this.welcomeCompany = null;
        this.searchService.searchTextInput = null;
        this.searchService.chipTextInput = [];
    }


}
